// The sentence in the corner, which is the whole header now.
//
//     Showing
//     Best for Sunbathing · mid July · midday
//
// Three words, three controls. The point of writing the state as a sentence
// rather than a row of chips is that it says what the map IS — a reader can tell
// what they are looking at without decoding a toolbar — and that every part of
// it is changeable in place. The active rating carries a solid accent underline,
// the two time words a dashed one, so "editable text" is legible as a shape.
//
// This module owns the words and the two TIME menus, which are short lists over
// data that already exists (sections.ts, manifest.buckets). The rating picker is
// a two-step surface with its own file, lens-menu.ts.
//
// Nothing here decides anything: a word opens a menu by setting state.menu, a
// row calls the mutator it was given, and the sentence itself re-renders as a
// subscriber. That is why there is no "close then apply" dance anywhere below.

import { $ } from '../dom.ts';
import { state, store } from '../state.ts';
import { VAR } from '../../lib/variables.ts';
import { MONTH_NAMES, PART_LABELS, parseSection, sectionLabel } from '../../lib/sections.ts';
import { conditionWords } from '../myday.ts';
import type { MenuName } from '../state.ts';

export interface SentenceDeps {
  setBucket: (bk: string) => void;
  showFrame: (idx: number) => void;
  pause: () => void;
  persist: () => void;
}

let deps: SentenceDeps;

export function initSentence(d: SentenceDeps): void {
  deps = d;
}

// "Early July" -> "early July". The sentence reads as prose, so only the proper
// noun keeps its capital.
function lower1(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

// The time-of-day word, from the manifest's own label ("Midday" -> "midday").
function bucketLabel(): string {
  const bk = state.manifest?.buckets.find((b) => b.key === state.bucket);
  return lower1(bk?.label || state.bucket);
}

// ── the words ───────────────────────────────────────────────────────────────
export function renderSentence(): void {
  const meta = VAR[state.variable];
  const compare = state.view === 'compare';
  const seed = meta?.custom ? state.daySeed : null;

  // "Best for Sunbathing" is the sentence; "Best for Temperature" is nonsense.
  // A rating answers "what is this place good FOR"; a measurement just says what
  // is painted, so the lead word drops and the sentence starts at the layer.
  // In a comparison the places are the subject and this line is its sub-line —
  // "for sunbathing · mid July · midday" — so the lead shortens to match.
  const lead = $('sn-lead');
  lead.hidden = !meta?.score;
  lead.textContent = compare ? 'for' : 'Best for';

  const lens = $('sn-lens');
  // A rating authored from a day is named by the day: "days like Split".
  const label = seed ? `days like ${seed.name}` : meta?.label || String(state.variable);
  lens.textContent = compare && !seed ? label.toLowerCase() : label;
  // The underline says which kind of thing is showing: solid accent for a
  // judgement the map is making, dashed for a plain measurement — the same
  // distinction the two time words carry.
  lens.classList.toggle('sn-lens-raw', !meta?.score);

  $('sn-when').textContent = state.dates.length ? lower1(sectionLabel(state.dates[state.dateIdx])) : '—';
  $('sn-bucket').textContent = bucketLabel();

  // The custom rating is the one lens a reader can get stuck in — it is theirs,
  // and it is not obvious that picking the word again leads back out. Say so,
  // and when it came from a day, say what the day was.
  const helper = $('sn-helper');
  const custom = !!meta?.custom;
  helper.hidden = !custom || compare;
  if (custom) {
    helper.textContent = seed
      ? `Rated against the day you named: ${conditionWords(seed.conditions, { prose: true })}. Pick that word to change the day or go back to a ready-made rating.`
      : 'Pick that word to go back to a ready-made rating. Your recipe is kept.';
  }
  $('sn-link').hidden = !compare;
}

// ── the two time menus ──────────────────────────────────────────────────────
// A row in any of these surfaces. One shape, shared with the lens menu through
// the .row primitive in style.css.
export function menuRow(label: string, hint: string, opts: { active?: boolean; trailing?: string } = {}): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'row';
  b.setAttribute('role', 'menuitem');
  const name = document.createElement('span');
  name.textContent = label;
  b.append(name);
  if (hint || opts.trailing) {
    const h = document.createElement('span');
    h.className = 'row-hint';
    h.textContent = opts.trailing ? `${hint} ${opts.trailing}`.trim() : hint;
    b.append(h);
  }
  if (opts.active) b.classList.add('active');
  return b;
}

// "mid July": a part across the top, then the twelve months. Picking either
// keeps the other, so the two decisions stay independent — which is the whole
// reason this is not one list of 36 rows.
//
// Built from a current value and a callback rather than from state, because two
// sentences ask this question: the map's ("mid July") and the landing's "I loved
// Split in late June".
export function whenPicker(cur: { month: number; part: number }, pick: (month: number, part: number) => void): HTMLElement[] {
  const parts = document.createElement('div');
  parts.className = 'seg';
  PART_LABELS.forEach((label, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'seg-btn';
    b.textContent = label;
    b.classList.toggle('active', cur.part === i + 1);
    b.addEventListener('click', () => pick(cur.month, i + 1));
    parts.appendChild(b);
  });

  const list = document.createElement('div');
  list.className = 'menu-list';
  MONTH_NAMES.forEach((name, i) => {
    const row = menuRow(name, '', { active: cur.month === i + 1 });
    row.addEventListener('click', () => pick(i + 1, cur.part));
    list.appendChild(row);
  });
  return [parts, list];
}

// "midday": the four time-of-day buckets, as words. BUCKETS carries an emoji
// per bucket and it is simply not read — the registry is untouched.
export function bucketPicker(cur: string, pick: (key: string) => void): HTMLElement {
  const list = document.createElement('div');
  list.className = 'menu-list';
  for (const bk of state.manifest?.buckets || []) {
    const row = menuRow(bk.label, `${String(bk.targetHour).padStart(2, '0')}:00 local`, { active: bk.key === cur });
    row.addEventListener('click', () => pick(bk.key));
    list.appendChild(row);
  }
  return list;
}

function renderWhenMenu(): void {
  const cur = state.dates.length ? parseSection(state.dates[state.dateIdx]) : { month: 1, part: 1 };
  $('when-menu').replaceChildren(...whenPicker(cur, goTo));
}

// A month and a part are an index into the 36: the keys are "MM-p" and sorted,
// so the arithmetic is the same one sections.ts guarantees.
function goTo(month: number, part: number): void {
  const idx = (month - 1) * 3 + (part - 1);
  if (idx < 0 || idx >= state.dates.length) return;
  deps.pause();
  deps.showFrame(idx);
  deps.persist();
  renderWhenMenu(); // the picker stays open: changing month then part is one errand
}

function renderBucketMenu(): void {
  $('bucket-menu').replaceChildren(bucketPicker(state.bucket, (key) => { deps.setBucket(key); store.set({ menu: null }); }));
}

// ── which surface is open ───────────────────────────────────────────────────
// Anchor a menu under the word that opens it, so it reads as that word
// expanding rather than as a panel appearing. Measured rather than a constant:
// the words' widths change with the rating's name.
export function anchorTo(box: HTMLElement, word: HTMLElement, base: HTMLElement = $('sentence')): void {
  const sentence = base.getBoundingClientRect();
  const w = word.getBoundingClientRect();
  box.style.marginLeft = `${Math.max(0, Math.round(w.left - sentence.left))}px`;
}

// The sentence's own render of state.menu: which word is lit, which surface is
// visible, and where it hangs. lens-menu.ts fills #lens-menu's contents; this
// decides whether it is on screen.
export function renderMenus(): void {
  const m = state.menu;
  const map: Record<string, { box: string; word: string }> = {
    lens: { box: 'lens-menu', word: 'sn-lens' },
    'lens-custom': { box: 'lens-menu', word: 'sn-lens' },
    'lens-raw': { box: 'lens-menu', word: 'sn-lens' },
    when: { box: 'when-menu', word: 'sn-when' },
    bucket: { box: 'bucket-menu', word: 'sn-bucket' },
  };

  for (const id of ['lens-menu', 'when-menu', 'bucket-menu']) $(id).hidden = true;
  for (const id of ['sn-lens', 'sn-when', 'sn-bucket']) {
    const el = $(id);
    el.classList.remove('open');
    el.setAttribute('aria-expanded', 'false');
  }

  if (m === 'when') renderWhenMenu();
  if (m === 'bucket') renderBucketMenu();

  const hit = m ? map[m] : null;
  if (!hit) return;
  const box = $(hit.box);
  const word = $(hit.word);
  box.hidden = false;
  anchorTo(box, word);
  word.classList.add('open');
  word.setAttribute('aria-expanded', 'true');
}

// ── wiring ──────────────────────────────────────────────────────────────────
export function wireSentence(): void {
  // A word toggles its own surface. Setting state.menu is the whole action —
  // renderMenus is a subscriber, so nothing here touches the DOM.
  const opens: [string, MenuName][] = [['sn-lens', 'lens'], ['sn-when', 'when'], ['sn-bucket', 'bucket']];
  for (const [id, name] of opens) {
    $(id).addEventListener('click', (e) => {
      e.stopPropagation(); // or the document closer below would undo this click
      // The lens surface has three steps, and any of them counts as "open" —
      // clicking the word while inside a step closes the lot rather than
      // stepping back, which is what a second click on an open menu should do.
      const openNow = name === 'lens' ? String(state.menu).startsWith('lens') : state.menu === name;
      store.set({ menu: openNow ? null : name });
    });
  }

  // Every summoned surface a click inside of must not close. The sentence's own
// three, the compare view's two, the landing sentence's picker and the search
// panel — the Layers panel keeps its own closer in chrome.ts.
const SURFACES = ['lens-menu', 'when-menu', 'bucket-menu', 'compare-mode-menu', 'compare-place-menu', 'dy-menu', 'search-menu'];

// Anything outside closes whatever is open.
  //
  // composedPath(), not target.contains(): a row that re-renders its own menu
  // (picking a month, which redraws the picker so the part stays selectable) has
  // DETACHED itself from the DOM by the time this bubbles to the document, so
  // `menu.contains(target)` is false and the click reads as "outside" — closing
  // the menu the reader is still using. The path is captured at dispatch and
  // survives the re-render.
  document.addEventListener('click', (e) => {
    if (!state.menu) return;
    if (state.menu === 'layers') return; // chrome.ts closes its own panel
    const path = e.composedPath();
    for (const id of SURFACES) {
      if (path.includes($(id))) return;
    }
    store.set({ menu: null });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && state.menu) store.set({ menu: null });
  });

  // The menus hang off measured positions, so a resize has to re-measure.
  window.addEventListener('resize', () => { if (state.menu) renderMenus(); });
}
